/**
 * AlertListView — "Show as alert list" view of the Reports page.
 *
 * Wraps /reports/alerts pagination around the same LatestAlertsTable row
 * renderer. Pagination controls use a classic first/prev/next/last pattern
 * so the operator can jump to the last page quickly when combing through a
 * dense range.
 */
import { useMemo, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  ChevronDoubleLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleRightIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { reportsApi, type ReportFilterInput } from '../../services/reportsApi';
import LatestAlertsTable from './LatestAlertsTable';
import { SkeletonChart } from '../common/Skeleton';

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

interface Props {
  filters: ReportFilterInput;
  onRowClick: (alertId: string) => void;
}

export default function AlertListView({ filters, onRowClick }: Props) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<(typeof PAGE_SIZE_OPTIONS)[number]>(25);

  // Re-keying on filters resets pagination — otherwise a filter change
  // leaves the user stranded on page 12 of a 3-page result.
  const resetKey = useMemo(() => JSON.stringify(filters), [filters]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useMemo(() => setPage(1), [resetKey]);

  const query = useQuery({
    queryKey: ['reports', 'alerts-list', filters, page, pageSize],
    queryFn: () => reportsApi.listAlerts(filters, page, pageSize),
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  });

  if (query.isError) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-400 text-sm">
        <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
        <span>Failed to load detections: {(query.error as Error)?.message}</span>
      </div>
    );
  }

  if (query.isLoading || !query.data) {
    return <SkeletonChart />;
  }

  const { data: rows, pagination } = query.data;

  return (
    <div className="space-y-4">
      <LatestAlertsTable
        rows={rows}
        onRowClick={onRowClick}
        title="Detections"
      />

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
        <div className="text-xs text-gray-500">
          Showing{' '}
          {pagination.totalItems === 0
            ? 0
            : (pagination.page - 1) * pagination.pageSize + 1}
          –
          {Math.min(pagination.page * pagination.pageSize, pagination.totalItems)}{' '}
          of {pagination.totalItems.toLocaleString()}
        </div>

        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-gray-400">
            Rows per page:
            <select
              className="px-2 py-1 bg-navy-800 border border-navy-600 rounded-md text-white text-xs focus:outline-none focus:ring-1 focus:ring-accent"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value) as (typeof PAGE_SIZE_OPTIONS)[number]);
                setPage(1);
              }}
            >
              {PAGE_SIZE_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>

          <div className="flex items-center gap-1">
            <PagerButton
              icon={ChevronDoubleLeftIcon}
              disabled={!pagination.hasPrevious}
              onClick={() => setPage(1)}
            />
            <PagerButton
              icon={ChevronLeftIcon}
              disabled={!pagination.hasPrevious}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            />
            <span className="px-2 text-xs text-gray-400">
              {pagination.page} / {Math.max(1, pagination.totalPages)}
            </span>
            <PagerButton
              icon={ChevronRightIcon}
              disabled={!pagination.hasNext}
              onClick={() => setPage((p) => p + 1)}
            />
            <PagerButton
              icon={ChevronDoubleRightIcon}
              disabled={!pagination.hasNext}
              onClick={() => setPage(Math.max(1, pagination.totalPages))}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function PagerButton({
  icon: Icon,
  disabled,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string }>;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="p-1.5 rounded-md border border-navy-700 bg-navy-800 text-gray-300 hover:bg-navy-700 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}
